import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RuleBuilder from '../components/segmentation/RuleBuilder';
import RulePreviewPanel from '../components/segmentation/RulePreviewPanel';
import {
  getLeadSegmentation,
  createLeadSegmentation,
  updateLeadSegmentation,
  refreshLeadSegmentation,
} from '../services/api';

const SEGMENT_TYPES = ['Approach', 'Industry', 'Vertical', 'UseCase', 'GeoMarket', 'CompanySize'];

const emptyEnvelope = () => ({
  version: 1,
  root: { kind: 'group', op: 'AND', children: [] },
});

export default function SegmentationDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';

  const [name, setName] = useState('');
  const [type, setType] = useState('Approach');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState('#1976d2');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [envelope, setEnvelope] = useState(emptyEnvelope());

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const nameMissing = submitted && !name.trim();

  useEffect(() => {
    if (isNew) return;
    let mounted = true;
    setLoading(true);
    getLeadSegmentation(id)
      .then((res) => {
        if (!mounted) return;
        const r = res.data;
        setName(r.name || '');
        setType(r.type || 'Approach');
        setDescription(r.description || '');
        setColor(r.color || '#1976d2');
        setAutoRefresh(!!r.autoRefresh);
        if (r.ruleJson) {
          try {
            setEnvelope(JSON.parse(r.ruleJson));
          } catch {
            setEnvelope(emptyEnvelope());
          }
        }
      })
      .catch((err) => {
        if (!mounted) return;
        if (err.response?.status === 404) {
          navigate('/segmentation', { replace: true });
          return;
        }
        setError(err.response?.data?.message || err.message);
      })
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [id, isNew]);

  const hasRule = useMemo(
    () => envelope?.root?.children?.length > 0,
    [envelope]
  );

  const onSave = async () => {
    setSubmitted(true);
    if (!name.trim()) {
      setError('Name is required.');
      return;
    }
    setSaving(true);
    setError(null);
    setInfo(null);
    try {
      const payload = {
        name: name.trim(),
        type,
        description: description || null,
        color: color || null,
        ruleJson: hasRule ? JSON.stringify(envelope) : null,
        autoRefresh,
      };
      if (isNew) {
        const res = await createLeadSegmentation(payload);
        const newId = res.data;
        navigate(`/segmentation/${newId}`, { replace: true });
        setInfo('Segment created.');
      } else {
        await updateLeadSegmentation(id, payload);
        setInfo('Segment saved.');
      }
    } catch (err) {
      const data = err.response?.data;
      setError(
        data?.errors?.length
          ? `${data.message || 'Save failed'}: ${data.errors.join('; ')}`
          : data?.message || err.message
      );
    } finally {
      setSaving(false);
    }
  };

  const onRefresh = async () => {
    if (isNew) return;
    setRefreshing(true);
    setError(null);
    try {
      const res = await refreshLeadSegmentation(id);
      const r = res.data;
      if (r.skipped) {
        setInfo(`Refresh skipped: ${r.skipReason}`);
      } else {
        setInfo(`Refreshed in ${r.durationMs} ms — matched ${r.matched}, added ${r.added}, removed ${r.removed}, kept ${r.kept}.`);
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/segmentation')} sx={{ mb: 2 }}>
        Back to Segments
      </Button>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ flex: 1 }}>
          {isNew ? 'New segment' : `Edit segment`}
        </Typography>
        {!isNew && (
          <Button
            startIcon={refreshing ? <CircularProgress size={14} /> : <RefreshIcon />}
            onClick={onRefresh}
            disabled={refreshing || !hasRule}
          >
            Refresh now
          </Button>
        )}
        <Button
          variant="contained"
          startIcon={<SaveOutlinedIcon />}
          onClick={onSave}
          disabled={saving}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {info && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo(null)}>{info}</Alert>}

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <TextField
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          fullWidth
          autoFocus
          placeholder="e.g. EU SaaS — qualified, mid-market"
          error={nameMissing}
          helperText={nameMissing ? 'Name is required.' : 'Used in lists, chips, and outreach personalisation.'}
          InputLabelProps={{ shrink: true }}
          sx={{ mb: 2 }}
        />
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            select
            label="Type"
            value={type}
            onChange={(e) => setType(e.target.value)}
            sx={{ minWidth: 180 }}
            required
          >
            {SEGMENT_TYPES.map((t) => (
              <MenuItem key={t} value={t}>{t}</MenuItem>
            ))}
          </TextField>
          <TextField
            type="color"
            label="Color"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            sx={{ width: 110 }}
            InputLabelProps={{ shrink: true }}
          />
          <FormControlLabel
            control={<Switch checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />}
            label="Auto refresh"
          />
        </Stack>
        <TextField
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
          multiline
          rows={2}
          sx={{ mt: 2 }}
        />
      </Paper>

      <Typography variant="subtitle1" sx={{ mb: 1 }}>Rule</Typography>
      <RuleBuilder value={envelope} onChange={setEnvelope} />

      <Box sx={{ mt: 2 }}>
        {hasRule ? (
          <RulePreviewPanel envelope={envelope} sampleSize={10} />
        ) : (
          <Alert severity="info">Add at least one condition to preview matches.</Alert>
        )}
      </Box>
    </Box>
  );
}
